import type { DateRange } from "@daypicker/react"
import * as React from "react"
import { draftPayload, rangeFromDraft, readBookingDraft } from "../bookingDraft"
import { BOOKING_DRAFT_TTL_MS } from "../bookingSession"
import type { StripeActions, StripeCheckout, StripeElement, StripeExpressCheckoutConfirmEvent } from "../stripe"
import type { Address, AddressLookupOption, AddressLookupPhase, AddressValue, Availability, BookingPane, BookingStatus, CalendarSnapshot, CustomerFields, DetailFieldName, JourneyStep, Phase, Product, Quote } from "../types"

export function useCheckoutState() {
    const initialDraft = React.useMemo(() => readBookingDraft(), [])
    const initialDraftRef = React.useRef(initialDraft)
    const lastDraftFingerprint = React.useRef(initialDraft ? JSON.stringify(draftPayload(initialDraft)) : "")
    const draftExpiresAt = React.useRef(initialDraft ? initialDraft.updatedAt + BOOKING_DRAFT_TTL_MS : 0)
    const instanceId = React.useId()
    const abortController = React.useRef<AbortController | null>(null)
    const availabilityController = React.useRef<AbortController | null>(null)
    const calendarController = React.useRef<AbortController | null>(null)
    const checkoutGeneration = React.useRef(0)
    const checkoutRef = React.useRef<StripeCheckout | null>(null)
    const actionsRef = React.useRef<StripeActions | null>(null)
    const billingElementRef = React.useRef<StripeElement | null>(null)
    const expressElementRef = React.useRef<StripeElement | null>(null)
    const paymentElementRef = React.useRef<StripeElement | null>(null)
    const billingMountRef = React.useRef<HTMLDivElement>(null)
    const expressMountRef = React.useRef<HTMLDivElement>(null)
    const paymentMountRef = React.useRef<HTMLDivElement>(null)
    const detailsPanelRef = React.useRef<HTMLElement>(null)
    const confirmPaymentRef = React.useRef<(event?: StripeExpressCheckoutConfirmEvent) => Promise<void>>(async () => { })
    const dateNoticeTimer = React.useRef<number | null>(null)
    const draftSaveTimer = React.useRef<number | null>(null)
    const draftExpiryTimer = React.useRef<number | null>(null)
    const draftPersistenceDisabled = React.useRef(false)
    const addressLookupController = React.useRef<AbortController | null>(null)
    const addressLookupCache = React.useRef(new Map<string, AddressLookupOption[]>())
    const addressLookupPostcode = React.useRef(initialDraft?.quote?.postcode || initialDraft?.postcode.trim().toUpperCase() || "")

    const [product, setProduct] = React.useState<Product | null>(null)
    const [productRetry, setProductRetry] = React.useState(0)
    const [activeSlug, setActiveSlug] = React.useState("")
    const [journeyStep, setJourneyStep] = React.useState<JourneyStep>("booking")
    const [bookingPane, setBookingPane] = React.useState<BookingPane>(initialDraft?.bookingPane ?? "selection")
    const [phase, setPhase] = React.useState<Phase>("loading")
    const [range, setRange] = React.useState<DateRange | undefined>(() => rangeFromDraft(initialDraft))
    const [activeDateField, setActiveDateField] = React.useState<"start" | "end">(() => range?.from ? "end" : "start")
    const [calendarOpen, setCalendarOpen] = React.useState(false)
    const [isMobile, setIsMobile] = React.useState(false)
    const [quantity, setQuantity] = React.useState(initialDraft?.quantity ?? 1)
    const [postcode, setPostcode] = React.useState(initialDraft?.postcode ?? "")
    const [settledPostcode, setSettledPostcode] = React.useState(postcode)
    const [availability, setAvailability] = React.useState<Availability | null>(null)
    const [calendarSnapshot, setCalendarSnapshot] = React.useState<CalendarSnapshot | null>(null)
    const [calendarPhase, setCalendarPhase] = React.useState<"loading" | "ready" | "error">("loading")
    const [calendarError, setCalendarError] = React.useState("")
    const [quote, setQuote] = React.useState<Quote | null>(null)
    const [selectedAddonIds, setSelectedAddonIds] = React.useState<string[]>(initialDraft?.selectedAddonIds ?? [])
    const [customer, setCustomer] = React.useState<CustomerFields>(initialDraft?.customer ?? { name: "", email: "", phone: "", companyName: "", deliveryInstructions: "" })
    const [addressOptions, setAddressOptions] = React.useState<AddressLookupOption[]>([])
    const [addressLookupPhase, setAddressLookupPhase] = React.useState<AddressLookupPhase>("idle")
    const [addressLookupMessage, setAddressLookupMessage] = React.useState("")
    const [selectedAddressId, setSelectedAddressId] = React.useState(initialDraft?.selectedAddressId ?? "")
    const [selectedDeliveryAddress, setSelectedDeliveryAddress] = React.useState<Address | null>(initialDraft?.selectedDeliveryAddress ?? null)
    const [manualDeliveryEntry, setManualDeliveryEntry] = React.useState(initialDraft?.manualDeliveryEntry ?? false)
    const [manualDeliveryAddress, setManualDeliveryAddress] = React.useState(initialDraft?.manualDeliveryAddress ?? { line1: "", line2: "", city: "" })
    const [billingAddress, setBillingAddress] = React.useState<AddressValue | null>(initialDraft?.billingAddress ?? null)
    const [billingComplete, setBillingComplete] = React.useState(initialDraft?.billingComplete ?? false)
    const [differentBilling, setDifferentBilling] = React.useState(initialDraft?.differentBilling ?? false)
    const [detailErrors, setDetailErrors] = React.useState<Partial<Record<DetailFieldName, string>>>({})
    const [validationAttempt, setValidationAttempt] = React.useState(0)
    const [error, setError] = React.useState("")
    const [clientSecret, setClientSecret] = React.useState("")
    const [confirmationToken, setConfirmationToken] = React.useState("")
    const [stripeReady, setStripeReady] = React.useState(false)
    const [stripeCanConfirm, setStripeCanConfirm] = React.useState(false)
    const [walletAvailable, setWalletAvailable] = React.useState<boolean | null>(false)
    const [bookingStatus, setBookingStatus] = React.useState<BookingStatus | null>(null)
    const [dateNotice, setDateNotice] = React.useState<{ id: number; message: string; target: "calendar" | "postcode" } | null>(null)
    const [draftRestored, setDraftRestored] = React.useState(!initialDraft || initialDraft.completedStep === "none")
    const [restoredCheckoutPhase, setRestoredCheckoutPhase] = React.useState<"loading" | "ready" | "error">(initialDraft?.checkout && initialDraft.completedStep !== "none" ? "loading" : "ready")
    const [restoredCheckoutStatus, setRestoredCheckoutStatus] = React.useState<BookingStatus["status"] | null>(null)
    const [restoredCheckoutRetry, setRestoredCheckoutRetry] = React.useState(0)
    const [savedDetailsFingerprint, setSavedDetailsFingerprint] = React.useState("")
    const detailsFingerprint = JSON.stringify({ customer, selectedDeliveryAddress, manualDeliveryEntry, manualDeliveryAddress, differentBilling, billingAddress, billingComplete })

    return {
        initialDraftRef,
        lastDraftFingerprint,
        draftExpiresAt,
        instanceId,
        abortController,
        availabilityController,
        calendarController,
        checkoutGeneration,
        checkoutRef,
        actionsRef,
        billingElementRef,
        expressElementRef,
        paymentElementRef,
        billingMountRef,
        expressMountRef,
        paymentMountRef,
        detailsPanelRef,
        confirmPaymentRef,
        dateNoticeTimer,
        draftSaveTimer,
        draftExpiryTimer,
        draftPersistenceDisabled,
        addressLookupController,
        addressLookupCache,
        addressLookupPostcode,
        product,
        setProduct,
        productRetry,
        setProductRetry,
        activeSlug,
        setActiveSlug,
        journeyStep,
        setJourneyStep,
        bookingPane,
        setBookingPane,
        phase,
        setPhase,
        range,
        setRange,
        activeDateField,
        setActiveDateField,
        calendarOpen,
        setCalendarOpen,
        isMobile,
        setIsMobile,
        quantity,
        setQuantity,
        postcode,
        setPostcode,
        settledPostcode,
        setSettledPostcode,
        availability,
        setAvailability,
        calendarSnapshot,
        setCalendarSnapshot,
        calendarPhase,
        setCalendarPhase,
        calendarError,
        setCalendarError,
        quote,
        setQuote,
        selectedAddonIds,
        setSelectedAddonIds,
        customer,
        setCustomer,
        addressOptions,
        setAddressOptions,
        addressLookupPhase,
        setAddressLookupPhase,
        addressLookupMessage,
        setAddressLookupMessage,
        selectedAddressId,
        setSelectedAddressId,
        selectedDeliveryAddress,
        setSelectedDeliveryAddress,
        manualDeliveryEntry,
        setManualDeliveryEntry,
        manualDeliveryAddress,
        setManualDeliveryAddress,
        billingAddress,
        setBillingAddress,
        billingComplete,
        setBillingComplete,
        differentBilling,
        setDifferentBilling,
        detailErrors,
        setDetailErrors,
        validationAttempt,
        setValidationAttempt,
        error,
        setError,
        clientSecret,
        setClientSecret,
        confirmationToken,
        setConfirmationToken,
        stripeReady,
        setStripeReady,
        stripeCanConfirm,
        setStripeCanConfirm,
        walletAvailable,
        setWalletAvailable,
        bookingStatus,
        setBookingStatus,
        dateNotice,
        setDateNotice,
        draftRestored,
        setDraftRestored,
        restoredCheckoutPhase,
        setRestoredCheckoutPhase,
        restoredCheckoutStatus,
        setRestoredCheckoutStatus,
        restoredCheckoutRetry,
        setRestoredCheckoutRetry,
        savedDetailsFingerprint,
        setSavedDetailsFingerprint,
        detailsFingerprint,
    }
}
